/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, LinearProgress } from '@mui/material';
import type { FC } from 'react';
import React from 'react';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { AlertBanner, ToolContainerDraggable } from '@opengeoweb/shared';
import { ServiceOptionsButtonConnect } from './ServiceOptionsConnect';
import { ServicePopupConnect } from './ServicePopup/ServicePopupConnect';
import SearchFieldConnect from './TimeSeriesSearchConnect';
import { DEFAULT_DIALOG_HEIGHT } from './TimeSeriesSelectList';
import { TimeSeriesSelectListConnect } from './TimeSeriesSelectListConnect';
import TimeSeriesServiceListConnect from './TimeSeriesServiceListConnect';
import { useTimeseriesTranslation } from '../../utils/i18n';
import { useGetServiceCollectionDetails } from '../../utils/timeseries-api/hooks';
import type { Parameter, Plot } from '../TimeSeries/types';

interface TimeSeriesSelectProps {
  panelId: string;
  onClose: () => void;
  onMouseDown: () => void;
  isOpen: boolean;
  order: number;
  selectPlot: Plot;
  handleAddOrRemoveClick: (
    parameter: Parameter,
    plotHasParameter: boolean,
  ) => void;
  services: TimeSeriesService[];
}

export const TimeSeriesSelect: FC<TimeSeriesSelectProps> = ({
  panelId,
  onClose,
  isOpen,
  onMouseDown,
  order,
  selectPlot,
  handleAddOrRemoveClick,
  services,
}) => {
  const { t } = useTimeseriesTranslation();
  const { isFetching, error } = useGetServiceCollectionDetails(
    services,
    isOpen,
  );

  return (
    <ToolContainerDraggable
      data-testid="TimeSeriesSelect"
      isOpen={isOpen}
      minHeight={200}
      minWidth={320}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      startPosition={{ top: 150, left: 100 }}
      startSize={{ width: 750, height: DEFAULT_DIALOG_HEIGHT }}
      title={`${t('timeseries-select-for')} ${selectPlot.title}`}
    >
      {!!isFetching && (
        <LinearProgress
          color="secondary"
          sx={{ width: '100%', position: 'absolute' }}
        />
      )}
      {!!error && (
        <Box sx={{ padding: '0 6px' }}>
          <AlertBanner shouldClose title={error?.message} />
        </Box>
      )}
      <Box
        sx={{
          padding: '6px 8px',
          height: `calc(100% - ${error ? 48 : 0}px)`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <SearchFieldConnect panelId={panelId} />
        <Box
          sx={{
            position: 'absolute',
            margin: '64px 0 0 calc(100% - 40px)',
            zIndex: 2,
            float: 'right',
          }}
        >
          <ServiceOptionsButtonConnect panelId={panelId} />
        </Box>
        <Box sx={{ paddingBottom: '6px', paddingRight: '32px' }}>
          <TimeSeriesServiceListConnect panelId={panelId} />
        </Box>

        <TimeSeriesSelectListConnect
          handleAddOrRemoveClick={handleAddOrRemoveClick}
          isOpen={isOpen}
          panelId={panelId}
          selectPlot={selectPlot}
          services={services}
        />
        <ServicePopupConnect panelId={panelId} />
      </Box>
    </ToolContainerDraggable>
  );
};
